import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

/**
 * Manages the feedback classification configuration: the classification
 * categories (replacing the old manual Excel list) and the voucher/courtesy
 * guidelines used to recommend a courtesy voucher amount per complaint.
 */
const useFeedbackClassification = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  // ---- Categories ----------------------------------------------------------

  const getCategories = useCallback(
    async (activeOnly = false) => {
      try {
        const params = new URLSearchParams();
        if (activeOnly) params.append('activeOnly', 'true');
        return await sendRequest({
          url: `/api/v1/feedbackClassification/categories?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Klassifikations-Kategorien');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const createCategory = useCallback(
    async (payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedbackClassification/categories`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Erstellen der Kategorie');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateCategory = useCallback(
    async (id, payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedbackClassification/categories/${id}`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren der Kategorie');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Loads the known "genannte Gründe" from the maintained overview (idempotent).
  const seedCategories = useCallback(async () => {
    try {
      return await sendRequest({
        url: `/api/v1/feedbackClassification/categories/seed`,
        method: 'POST',
        headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: {},
      });
    } catch (err) {
      toast.error('Fehler beim Laden der bekannten Kategorien');
      throw err;
    }
  }, [authCtx, sendRequest]);

  const deleteCategory = useCallback(
    async (id) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedbackClassification/categories/${id}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Löschen der Kategorie');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // ---- Voucher guidelines --------------------------------------------------

  const getGuidelines = useCallback(
    async (activeOnly = false) => {
      try {
        const params = new URLSearchParams();
        if (activeOnly) params.append('activeOnly', 'true');
        return await sendRequest({
          url: `/api/v1/feedbackClassification/guidelines?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Gutschein-Richtlinien');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const createGuideline = useCallback(
    async (payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedbackClassification/guidelines`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Erstellen der Richtlinie');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateGuideline = useCallback(
    async (id, payload) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedbackClassification/guidelines/${id}`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren der Richtlinie');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const deleteGuideline = useCallback(
    async (id) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedbackClassification/guidelines/${id}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Löschen der Richtlinie');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    seedCategories,
    getGuidelines,
    createGuideline,
    updateGuideline,
    deleteGuideline,
  };
};

export default useFeedbackClassification;
